import { isLocale, type Locale } from '@/i18n';

/**
 * **A language change reaches the person's other tabs** (docs/I18N.md §8).
 *
 * The client's locale comes from the root layout (`LocaleProvider`,
 * src/lib/locale-context.tsx), which a soft navigation does not render again,
 * while a server component formats in the locale it resolves per request
 * (`getT`, src/lib/viewer.ts). So after a change on /account, every other open
 * tab would mix two languages until it loaded again. The language section
 * writes the new setting under {@link LOCALE_SYNC_KEY} after a save; the
 * browser fires a `storage` event in every OTHER tab of the same origin, and
 * the listener next to the provider reloads that tab once when the setting
 * differs from the one it was rendered with. A tab on another device learns
 * at its next full load.
 *
 * Local storage can be missing or refuse (a private window, blocked site
 * data): then the write and the listener do nothing, and the other tabs keep
 * their language until they load again, as before.
 */

/** The local storage key the language section writes. */
export const LOCALE_SYNC_KEY = 'tesserano-locale';

/**
 * The value written after a save: the new setting (null: follow the browser)
 * and the time of the save. The time makes every save a new value, so a save
 * that repeats the last stored setting (after a change made on another device)
 * still fires the event.
 */
export function localeSyncValue(setting: Locale | null, at: number): string {
  return JSON.stringify({ setting, at });
}

/**
 * The decision a tab takes on the event: reload when the stored value names a
 * setting that differs from the one the page was rendered with; nothing when
 * it is the same, or when the value is missing or unreadable.
 */
export function shouldReloadForLocale(stored: string | null, rendered: Locale | null): boolean {
  const setting = storedSetting(stored);
  return setting !== undefined && setting !== rendered;
}

/** The setting a stored value names; `undefined` when it names none. */
function storedSetting(stored: string | null): Locale | null | undefined {
  if (stored === null) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(stored);
  } catch {
    return undefined;
  }
  if (typeof parsed !== 'object' || parsed === null || !('setting' in parsed)) return undefined;
  const setting = (parsed as { setting: unknown }).setting;
  if (setting === null) return null;
  return isLocale(setting) ? setting : undefined;
}

/** Tell the other tabs of this browser about a saved setting. Does nothing
 *  where local storage is unavailable. */
export function announceLocaleSetting(setting: Locale | null): void {
  try {
    window.localStorage.setItem(LOCALE_SYNC_KEY, localeSyncValue(setting, Date.now()));
  } catch {
    // No storage in this browser context: the other tabs learn at their next load.
  }
}
